<script setup lang="ts">
import { computed } from "vue";
import { storeToRefs } from "pinia";
import { useResultsV2Store } from "@stores/survey/resultsV2/resultsV2";
import { useResultsV2CardsStore } from "@stores/survey/resultsV2/resultsV2.cards";
import { useScrollSpy } from "@/composables/useScrollSpy";
import { languageDirection } from "@/utils/textDirection";
import { revealAndScrollToCard } from "@/utils/scrollCardIntoView";
import { stripHtml } from "@utils/toolBox";

const store = useResultsV2Store();
const { sectionCards } = storeToRefs(useResultsV2CardsStore());

const textDirection = computed(() => languageDirection(store.displayLanguage));

interface SectionCard {
  id: number;
  content?: { title?: string };
}

const sections = computed(() =>
  (sectionCards.value as SectionCard[]).map((card) => ({
    id: card.id,
    title: stripHtml(card.content?.title) || "Untitled section",
  })),
);

const { activeId } = useScrollSpy(
  computed(() => sections.value.map((section) => section.id)),
  {
    resolveContainer: () =>
      document.querySelector("[data-results-card-list]")?.closest<HTMLElement>(".overflow-y-auto") ||
      null,
    elementId: (id) => `results-card-${id}`,
  },
);
</script>

<template>
  <nav v-if="sections.length" class="h-full overflow-y-auto py-1 pr-2">
    <p class="px-3 pb-2 heading-label">
      Contents
    </p>
    <ul class="space-y-0.5">
      <li v-for="section in sections" :key="section.id">
        <button
          :dir="textDirection"
          class="w-full rounded-r-lg border-l-2 px-3 py-1.5 text-left text-13 transition-colors"
          :class="
            section.id === activeId
              ? 'border-ring bg-white font-medium text-primary shadow-xs'
              : 'border-transparent text-muted-foreground hover:bg-white hover:text-primary'
          "
          @click="revealAndScrollToCard(section.id)">
          {{ section.title }}
        </button>
      </li>
    </ul>
  </nav>
</template>
